/**
 * The Add Log Source form's chrome — everything on the page that is the same
 * whether the form has loaded, is loading, or blew up.
 *
 * **Eager**, and shared with the skeleton for the reason the Add Collector
 * wizard's frame is: the form behind it is `lazy()`, so a skeleton drawing its
 * own copy of the title or the card's explanation would be a second place for
 * both to drift.
 *
 * Everything here is knowable without a server response, which is why it renders
 * for real in the loading state (P-05 §1). The one exception is the subtitle,
 * which names the device type and therefore waits on the options read.
 *
 * One screen, no step rail: adding a log source ends at submit, on the
 * collector's detail page. There is no live tail to verify against.
 */
import { SectionDescription, SectionTitle } from '@/features/app-shell/section'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'

import type { ReactNode } from 'react'

type AddLogSourceFrameProps = {
	/**
	 * The line under the title — `<Device type> · Network Logs · received via
	 * collector`. A node rather than a string so the skeleton can hand it a
	 * placeholder: it is the one part of this chrome that waits on a read.
	 */
	subtitle: ReactNode
	/** The form's body — its card, and anything beside it. */
	children: ReactNode
	/**
	 * The form's actions, **below** the card rather than in a `CardFooter` —
	 * the design frames put them outside it.
	 */
	footer: ReactNode
}

export const AddLogSourceFrame = ({
	subtitle,
	children,
	footer,
}: AddLogSourceFrameProps) => {
	return (
		<div className="flex flex-col gap-6">
			<div className="flex min-w-0 flex-col gap-1">
				<SectionTitle>Add network log source</SectionTitle>
				<SectionDescription>{subtitle}</SectionDescription>
			</div>

			<div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
				{children}

				<div className="flex items-center justify-end gap-3">{footer}</div>
			</div>
		</div>
	)
}

/**
 * The form's card, with the one sentence that says what this screen is
 * for.
 *
 * "Parsing is handled out of the box" is load-bearing rather than reassurance:
 * every other log platform makes the operator write a parser at this point, and
 * somebody who does not read it here will go looking for the parser field.
 */
export const RegisterSourceCard = ({ children }: { children: ReactNode }) => {
	return (
		<Card>
			<CardHeader>
				<CardTitle>Register source</CardTitle>
				<CardDescription>
					Tell AIR® where to receive this source and on which collector. Parsing
					is handled out of the box.
				</CardDescription>
			</CardHeader>

			<CardContent className="flex flex-col gap-5">{children}</CardContent>
		</Card>
	)
}
